<template>
  <div class="jobs-catalogue-page">
    <!-- ── Page Header ── -->
    <div class="ms-page-header">
      <div class="ms-page-header__content">
        <div class="ms-page-header__icon">
          <i class="fa-solid fa-diagram-project"></i>
        </div>
        <div>
          <p class="ms-page-header__breadcrumb">
            Platform Settings <span class="ms-breadcrumb-sep">/</span> Jobs Catalogue
          </p>
          <h1 class="ms-page-header__title">Jobs Catalogue & Blueprints</h1>
          <p class="ms-page-header__subtitle">
            Configure operational workflow blueprints, dynamic role assignments, and lead conversion triggers
          </p>
        </div>
      </div>
    </div>

    <!-- ── Summary Cards ── -->
    <div class="pp-summary-row">
      <div class="pp-stat-card">
        <div class="pp-stat-icon" style="background-color: #00A0C1;">
          <i class="fas fa-box-open"></i>
        </div>
        <div class="pp-stat-body">
          <span class="pp-stat-value">{{ products.length }}</span>
          <span class="pp-stat-label">Total Products</span>
        </div>
      </div>

      <div class="pp-stat-card">
        <div class="pp-stat-icon" style="background-color: #10b981;">
          <i class="fas fa-diagram-project"></i>
        </div>
        <div class="pp-stat-body">
          <div class="d-flex align-items-baseline gap-2">
            <span class="pp-stat-value">{{ linkedProductsCount }}</span>
            <span class="pp-stat-badge">{{ coveragePercentage }}%</span>
          </div>
          <span class="pp-stat-label">Linked Blueprints</span>
        </div>
      </div>

      <div class="pp-stat-card">
        <div class="pp-stat-icon" style="background-color: #f59e0b;">
          <i class="fas fa-clock"></i>
        </div>
        <div class="pp-stat-body">
          <span class="pp-stat-value">{{ unconfiguredProductsCount }}</span>
          <span class="pp-stat-label">Pending Config</span>
        </div>
      </div>

      <div class="pp-stat-card">
        <div class="pp-stat-icon" style="background-color: #3b82f6;">
          <i class="fas fa-tasks"></i>
        </div>
        <div class="pp-stat-body">
          <span class="pp-stat-value">{{ totalTasksCount }}</span>
          <span class="pp-stat-label">Blueprint Tasks</span>
        </div>
      </div>

      <div class="pp-stat-card">
        <div class="pp-stat-icon" style="background-color: #10b981;">
          <i class="fas fa-bolt"></i>
        </div>
        <div class="pp-stat-body">
          <span class="pp-stat-value">{{ activeJobs.length }}</span>
          <span class="pp-stat-label">Active / Staged Jobs</span>
        </div>
      </div>
    </div>

    <!-- ── Main Table Card ── -->
    <div class="pp-card">
      <!-- Tabs Bar (Matching Organisations Standard) -->
      <div class="org-tabs-container">
        <div class="org-tabs-nav">
          <button
            type="button"
            class="org-tab-btn"
            :class="{ active: activeTab === 'products' }"
            @click="activeTab = 'products'"
          >
            <i class="fas fa-boxes-stacked me-2"></i> Products & Services Status
            <span class="org-tab-counter-badge">{{ products.length }}</span>
          </button>
          <button
            type="button"
            class="org-tab-btn"
            :class="{ active: activeTab === 'blueprints' }"
            @click="activeTab = 'blueprints'"
          >
            <i class="fas fa-project-diagram me-2"></i> Configured Blueprints
            <span class="org-tab-counter-badge">{{ blueprints.length }}</span>
          </button>
          <button
            type="button"
            class="org-tab-btn"
            :class="{ active: activeTab === 'active_jobs' }"
            @click="activeTab = 'active_jobs'"
          >
            <i class="fas fa-bolt me-2"></i> Staged & Active Jobs
            <span class="org-tab-counter-badge">{{ activeJobs.length }}</span>
          </button>
        </div>
      </div>

      <!-- Toolbar -->
      <div class="pp-card__toolbar">
        <div class="d-flex align-items-center flex-wrap gap-2">
          <div class="pp-search-wrap">
            <input
              v-model.trim="searchQuery"
              type="text"
              class="pp-search-input"
              :placeholder="activeTab === 'active_jobs' ? '🔍 Search client, job title, or source...' : '🔍 Search products or blueprints...'"
            />
          </div>

          <button v-if="activeTab !== 'active_jobs'" type="button" class="pp-btn pp-btn--primary" @click="openCreateModal()">
            <i class="fas fa-plus me-1"></i>
            <span>New Blueprint</span>
          </button>
        </div>

        <div class="pp-toolbar-right d-flex flex-wrap align-items-center gap-2">
          <!-- Status Filter -->
          <div v-if="activeTab === 'products'" class="d-flex align-items-center">
            <span class="p-2 filterTitle">Status:</span>
            <select v-model="productStatusFilter" class="form-select form-select-sm shadow-none filterOptions bordered-select">
              <option value="all">All Products</option>
              <option value="linked">Linked</option>
              <option value="unconfigured">Not Configured</option>
            </select>
          </div>

          <div v-if="activeTab === 'blueprints'" class="d-flex align-items-center">
            <span class="p-2 filterTitle">Status:</span>
            <select v-model="blueprintStatusFilter" class="form-select form-select-sm shadow-none filterOptions bordered-select">
              <option value="all">All Statuses</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>

          <div v-if="activeTab === 'active_jobs'" class="d-flex align-items-center">
            <span class="p-2 filterTitle">Status:</span>
            <select v-model="activeJobStatusFilter" class="form-select form-select-sm shadow-none filterOptions bordered-select">
              <option value="all">All Statuses</option>
              <option value="Pending">Pending</option>
              <option value="In Progress">In Progress</option>
              <option value="Completed">Completed</option>
              <option value="Cancelled">Cancelled</option>
            </select>
          </div>

          <!-- Rows per page -->
          <div class="d-flex align-items-center">
            <span class="p-2 filterTitle">Rows per page:</span>
            <select
              v-model="pageSize"
              class="form-select form-select-sm shadow-none filterOptions bordered-select"
              style="width: auto;"
            >
              <option v-for="row in rowsPerPageList" :value="row.value" :key="row.value">{{ row.text }}</option>
            </select>
          </div>

          <!-- Top Pagination Control -->
          <div class="pagination">
            <button
              type="button"
              @click="previousPage()"
              :disabled="currentPage === 1"
              class="pagination-arrow"
              title="Previous Page"
            >
              <i class="fas fa-chevron-left"></i>
            </button>
            <span class="pagination-label">
              <strong>{{ currentPaginationLabel }}</strong> of {{ currentTotalItems }}
            </span>
            <button
              type="button"
              @click="nextPage()"
              :disabled="currentPage >= currentTotalPages || currentTotalItems === 0"
              class="pagination-arrow"
              title="Next Page"
            >
              <i class="fas fa-chevron-right"></i>
            </button>
          </div>

          <!-- Refresh Data Button -->
          <button
            type="button"
            class="pp-page-btn"
            :disabled="loading"
            title="Refresh Data"
            style="border: 1px solid #dee2e6; border-radius: 8px; padding: 7px 12px; background: #fff; height: 38px; display: inline-flex; align-items: center; justify-content: center;"
            @click="refreshData"
          >
            <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          </button>
        </div>
      </div>

      <!-- ── TAB 1: Products & Services Table ── -->
      <div v-show="activeTab === 'products'" class="pp-table-wrap">
        <table class="pp-table">
          <thead>
            <tr>
              <th>Product / Service</th>
              <th>Auth Item Code</th>
              <th>Category</th>
              <th>Base Price</th>
              <th>Blueprint Status</th>
              <th style="text-align: right;">Action</th>
            </tr>
          </thead>

          <tbody v-if="loading && !products.length">
            <tr>
              <td colspan="6" class="pp-empty-row">
                <div class="pp-empty-state">
                  <i class="fas fa-circle-notch fa-spin"></i>
                  <span>Loading products & blueprints...</span>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else-if="!paginatedProducts.length">
            <tr>
              <td colspan="6" class="pp-empty-row">
                <div class="pp-empty-state">
                  <i class="fas fa-inbox"></i>
                  <span>No products found matching your filter.</span>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr v-for="prod in paginatedProducts" :key="prod.product_id">
              <td class="pp-cell-name">
                <div class="d-flex align-items-center gap-2">
                  <div class="pp-prod-icon">
                    <i class="fas fa-cube"></i>
                  </div>
                  <div>
                    <span class="pp-product-name">{{ prod.display_name || prod.product_name }}</span>
                    <div v-if="prod.description" class="pp-product-subdesc text-truncate" style="max-width: 320px;" :title="prod.description">
                      {{ prod.description }}
                    </div>
                  </div>
                </div>
              </td>

              <td>
                <span v-if="getAuthItemCode(prod)" class="pp-code-badge" :title="getAuthItemCode(prod)">
                  {{ getAuthItemCode(prod) }}
                </span>
                <span v-else class="pp-na">N/A</span>
              </td>

              <td>
                <span class="pp-category-text">{{ prod.category_name || 'Standard' }}</span>
              </td>

              <td>
                <span class="pp-price-badge">{{ formatCurrency(prod.price) }}</span>
              </td>

              <td>
                <div v-if="isProductConfigured(prod)">
                  <span
                    class="aj-badge-status aj-badge-status--completed"
                    style="cursor: pointer;"
                    title="Click to view blueprint details"
                    @click="openViewBlueprintForProduct(prod)"
                  >
                    <i class="fas fa-check-circle me-1"></i>
                    {{ getProductBlueprintsCount(prod) > 1 ? `Configured (${getProductBlueprintsCount(prod)})` : 'Configured' }}
                  </span>
                  <div
                    class="pp-blueprint-subtext text-truncate modal-link-highlight mt-1"
                    style="max-width: 220px; cursor: pointer; color: #008298; font-weight: 500;"
                    :title="getProductBlueprintTitle(prod)"
                    @click="openViewBlueprintForProduct(prod)"
                  >
                    {{ getProductBlueprintTitle(prod) }}
                  </div>
                </div>
                <div v-else>
                  <span class="aj-badge-status aj-badge-status--notconfigured">
                    <i class="fas fa-clock me-1"></i> Not Configured
                  </span>
                </div>
              </td>

              <td class="pp-actions">
                <div class="action-button-container">
                  <button
                    v-if="isProductConfigured(prod)"
                    type="button"
                    class="btn-svg-icon"
                    title="View Blueprint Details"
                    @click="openViewBlueprintForProduct(prod)"
                  >
                    <i class="fa-solid fa-eye icon-svg-fa"></i>
                  </button>
                  <button
                    v-if="isProductConfigured(prod)"
                    type="button"
                    class="btn-svg-icon"
                    title="Edit Blueprint"
                    @click="openEditBlueprintForProduct(prod)"
                  >
                    <img src="@/assets/img/icons/universal_edit.svg" alt="Edit Blueprint" class="icon-svg" />
                  </button>
                  <button
                    type="button"
                    class="btn-svg-icon"
                    :title="isProductConfigured(prod) ? 'Add Another Blueprint for this Product' : 'Create Blueprint'"
                    @click="openCreateModal(prod.product_id || prod.id)"
                  >
                    <i class="fa-solid fa-plus icon-svg-fa"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ── TAB 2: Configured Blueprints Table ── -->
      <div v-show="activeTab === 'blueprints'" class="pp-table-wrap">
        <table class="pp-table">
          <thead>
            <tr>
              <th>Blueprint Title</th>
              <th>Auth Code</th>
              <th>Total Tasks</th>
              <th>Execution Breakdown</th>
              <th>Status</th>
              <th style="text-align: right;">Action</th>
            </tr>
          </thead>

          <tbody v-if="loading && !blueprints.length">
            <tr>
              <td colspan="6" class="pp-empty-row">
                <div class="pp-empty-state">
                  <i class="fas fa-circle-notch fa-spin"></i>
                  <span>Loading blueprints...</span>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else-if="!paginatedBlueprints.length">
            <tr>
              <td colspan="6" class="pp-empty-row">
                <div class="pp-empty-state">
                  <i class="fas fa-project-diagram"></i>
                  <span>No blueprints configured yet. Click "+ New Blueprint" to create one.</span>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr v-for="bp in paginatedBlueprints" :key="bp.uuid || bp.id">
              <td class="pp-cell-name">
                <div class="d-flex align-items-center gap-2">
                  <div class="pp-bp-icon" style="cursor: pointer;" title="View Blueprint Details" @click="openViewBlueprintModal(bp.uuid || bp.id)">
                    <i class="fas fa-project-diagram"></i>
                  </div>
                  <div>
                    <a
                      href="#"
                      class="pp-product-name text-decoration-none"
                      style="cursor: pointer; color: #00363f; font-weight: 600;"
                      title="Click to view blueprint details"
                      @click.prevent="openViewBlueprintModal(bp.uuid || bp.id)"
                    >
                      {{ bp.title }}
                    </a>
                    <div v-if="bp.description" class="pp-product-subdesc text-truncate" style="max-width: 320px;" :title="bp.description">
                      {{ bp.description }}
                    </div>
                  </div>
                </div>
              </td>

              <td>
                <span v-if="getAuthItemCode(bp)" class="pp-code-badge" :title="getAuthItemCode(bp)">
                  {{ getAuthItemCode(bp) }}
                </span>
                <span v-else class="pp-na">N/A</span>
              </td>

              <td>
                <span class="pp-task-count-badge">
                  <i class="fas fa-list-check me-1"></i>
                  {{ bp.tasks_count !== undefined ? bp.tasks_count : (bp.tasks ? bp.tasks.length : 0) }} Tasks
                </span>
              </td>

              <td>
                <div class="d-flex align-items-center gap-1 flex-wrap">
                  <span class="pp-seq-badge" title="Sequential Tasks">
                    <i class="fas fa-arrow-down-1-9 me-1"></i>
                    {{ countSequential(bp) }} Seq
                  </span>
                  <span class="pp-conc-badge" title="Concurrent Tasks">
                    <i class="fas fa-arrows-split-up-and-left me-1"></i>
                    {{ countConcurrent(bp) }} Conc
                  </span>
                </div>
              </td>

              <td>
                <span v-if="bp.is_active" class="aj-badge-status aj-badge-status--completed">
                  <i class="fas fa-check me-1"></i> Active
                </span>
                <span v-else class="aj-badge-status aj-badge-status--cancelled">
                  <i class="fas fa-pause me-1"></i> Inactive
                </span>
              </td>

              <td class="pp-actions">
                <div class="action-button-container">
                  <button
                    type="button"
                    class="btn-svg-icon"
                    title="View Blueprint Details"
                    @click="openViewBlueprintModal(bp.uuid || bp.id)"
                  >
                    <i class="fa-solid fa-eye icon-svg-fa"></i>
                  </button>
                  <button
                    type="button"
                    class="btn-svg-icon"
                    title="Edit Blueprint"
                    @click="openEditModal(bp.uuid || bp.id)"
                  >
                    <img src="@/assets/img/icons/universal_edit.svg" alt="Edit" class="icon-svg" />
                  </button>
                  <button
                    type="button"
                    class="btn-svg-icon"
                    title="Clone / Duplicate Blueprint"
                    @click="cloneBlueprint(bp.uuid || bp.id)"
                  >
                    <i class="fa-solid fa-clone icon-svg-fa"></i>
                  </button>
                  <button
                    type="button"
                    class="btn-svg-icon btn-svg-icon--delete"
                    title="Delete Blueprint"
                    @click="confirmDelete(bp)"
                  >
                    <img src="@/assets/img/icons/universal_delete.svg" alt="Delete" class="icon-svg" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ── TAB 3: Staged & Active Jobs Table ── -->
      <div v-show="activeTab === 'active_jobs'" class="pp-table-wrap">
        <table class="pp-table">
          <thead>
            <tr>
              <th>Client Organisation</th>
              <th>Active Job & Blueprint</th>
              <th>Source</th>
              <th>Tasks Breakdown</th>
              <th>Status</th>
              <th>Health</th>
              <th>Date Staged</th>
              <th style="text-align: right;">Action</th>
            </tr>
          </thead>

          <tbody v-if="loading && !activeJobs.length">
            <tr>
              <td colspan="8" class="pp-empty-row">
                <div class="pp-empty-state">
                  <i class="fas fa-circle-notch fa-spin"></i>
                  <span>Loading active jobs...</span>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else-if="!paginatedActiveJobs.length">
            <tr>
              <td colspan="8" class="pp-empty-row">
                <div class="pp-empty-state">
                  <i class="fas fa-bolt"></i>
                  <span>No active or staged jobs found. Converting a Lead will automatically generate operational jobs here.</span>
                </div>
              </td>
            </tr>
          </tbody>

          <tbody v-else>
            <tr v-for="job in paginatedActiveJobs" :key="job.id">
              <td>
                <div class="d-flex align-items-center gap-2">
                  <div style="background-color: #f1f5f9; color: #0f172a; width: 32px; height: 32px; border-radius: 8px; display: flex; align-items: center; justify-content: center; font-size: 0.9rem;">
                    <i class="fas fa-building"></i>
                  </div>
                  <div>
                    <strong class="d-block text-dark">{{ job.organisation_name || 'Standard Client' }}</strong>
                    <span v-if="job.organisation_uuid" class="text-muted small font-monospace" style="font-size: 0.72rem;">{{ job.organisation_uuid.substring(0, 8) }}...</span>
                  </div>
                </div>
              </td>

              <td>
                <strong class="d-block text-dark">{{ job.title }}</strong>
                <span class="pp-blueprint-subtext" v-if="job.blueprint">
                  <i class="fas fa-project-diagram me-1"></i> {{ job.blueprint.title }}
                  <span v-if="job.blueprint.auth_item_code" class="badge bg-light text-dark border ms-1">{{ job.blueprint.auth_item_code }}</span>
                </span>
              </td>

              <td>
                <span v-if="job.lead_id" class="badge" style="background-color: #e0f2fe; color: #0369a1; font-weight: 500;">
                  <i class="fas fa-user-plus me-1"></i> Lead Converted
                  <span v-if="job.lead_title" class="d-block small text-muted" style="font-size: 0.7rem;">{{ job.lead_title }}</span>
                </span>
                <span v-else-if="job.contract_id" class="badge" style="background-color: #ecfdf5; color: #047857; font-weight: 500;">
                  <i class="fas fa-file-signature me-1"></i> Contract Signed
                </span>
                <span v-else class="badge bg-light text-secondary">Direct</span>
              </td>

              <td>
                <div class="d-flex flex-column gap-1" style="min-width: 130px;">
                  <div class="d-flex justify-content-between small fw-bold">
                    <span>{{ job.completed_tasks_count || 0 }} / {{ job.tasks_count || 0 }} Tasks</span>
                    <span class="text-muted">{{ calculateProgressPercent(job) }}%</span>
                  </div>
                  <div class="progress" style="height: 6px; background-color: #e2e8f0; border-radius: 4px;">
                    <div
                      class="progress-bar"
                      role="progressbar"
                      :style="{ width: calculateProgressPercent(job) + '%', backgroundColor: '#00A0C1' }"
                    ></div>
                  </div>
                </div>
              </td>

              <td>
                <span class="aj-badge-status" :class="'aj-badge-status--' + (job.job_status || 'pending').toLowerCase().replace(' ', '')">
                  {{ job.job_status || 'Pending' }}
                </span>
              </td>

              <td>
                <span class="aj-badge-health" :class="'aj-badge-health--' + (job.health_indicator || 'ontrack').toLowerCase().replace(' ', '')">
                  <i class="fas fa-heartbeat me-1"></i> {{ job.health_indicator || 'On Track' }}
                </span>
              </td>

              <td>
                <span class="text-muted small">{{ formatDate(job.created_at) }}</span>
              </td>

              <td class="pp-actions" style="text-align: right;">
                <div class="action-button-container">
                  <button
                    type="button"
                    class="btn-svg-icon"
                    title="View Task Hierarchy & Details"
                    @click="openActiveJobModal(job.id)"
                  >
                    <i class="fa-solid fa-list-check icon-svg-fa"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- ── Active Job Details Modal ── -->
    <Transition name="pp-modal-fade">
      <div v-if="showActiveJobModal" class="pp-modal-overlay" @click.self="closeActiveJobModal">
        <div class="pp-modal pp-modal--wide" style="max-width: 900px;">
          <div class="pp-modal__header d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-3">
              <div class="aj-modal-header-icon">
                <i class="fas fa-bolt"></i>
              </div>
              <div>
                <h5 class="aj-modal-title">{{ selectedActiveJob?.title || 'Active Job Details' }}</h5>
                <div class="d-flex align-items-center gap-2 flex-wrap small text-muted mt-1">
                  <span class="aj-header-meta"><i class="fas fa-building me-1"></i> {{ selectedActiveJob?.organisation_name || 'Client Organisation' }}</span>
                  <span class="aj-header-sep">•</span>
                  <span class="aj-header-meta"><i class="fas fa-calendar-alt me-1"></i> Created {{ formatDate(selectedActiveJob?.created_at) }}</span>
                  <span class="aj-header-sep">•</span>
                  <span class="aj-badge-status" :class="'aj-badge-status--' + (selectedActiveJob?.job_status || 'pending').toLowerCase().replace(' ', '')">
                    {{ selectedActiveJob?.job_status || 'Pending' }}
                  </span>
                  <span class="aj-badge-health" :class="'aj-badge-health--' + (selectedActiveJob?.health_indicator || 'ontrack').toLowerCase().replace(' ', '')">
                    <i class="fas fa-heartbeat me-1"></i> {{ selectedActiveJob?.health_indicator || 'On Track' }}
                  </span>
                </div>
              </div>
            </div>
            <button type="button" class="pp-modal__close" @click="closeActiveJobModal">&times;</button>
          </div>
          <div class="pp-modal__divider"></div>

          <div class="pp-modal__body p-4" style="max-height: 72vh; overflow-y: auto;">
            <div v-if="loadingActiveJobDetails" class="text-center py-5">
              <i class="fas fa-circle-notch fa-spin fa-2x text-muted mb-2"></i>
              <p class="text-muted small">Loading active job structure and tasks...</p>
            </div>

            <div v-else-if="selectedActiveJob">
              <!-- Job Meta Cards -->
              <div class="row g-3 mb-4">
                <div class="col-12 col-md-4">
                  <div
                    class="aj-card-meta"
                    :style="selectedActiveJob.blueprint?.uuid ? 'cursor: pointer;' : ''"
                    :title="selectedActiveJob.blueprint?.uuid ? 'Click to inspect blueprint template' : ''"
                    @click="selectedActiveJob.blueprint?.uuid && openViewBlueprintModal(selectedActiveJob.blueprint.uuid)"
                  >
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="aj-card-label">Source Blueprint</span>
                      <i v-if="selectedActiveJob.blueprint?.uuid" class="fas fa-arrow-up-right-from-square text-muted small" style="font-size: 0.75rem;"></i>
                    </div>
                    <div class="d-flex align-items-center gap-2 mt-1">
                      <strong class="aj-card-val text-truncate" :title="selectedActiveJob.blueprint?.title">
                        {{ selectedActiveJob.blueprint?.title || 'Standard Blueprint' }}
                      </strong>
                      <span v-if="selectedActiveJob.blueprint?.auth_item_code" class="aj-code-tag">
                        {{ selectedActiveJob.blueprint.auth_item_code }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-md-4">
                  <div class="aj-card-meta">
                    <span class="aj-card-label">Trigger Source</span>
                    <div class="mt-1">
                      <span v-if="selectedActiveJob.lead_id" class="aj-trigger-badge aj-trigger-badge--lead">
                        <i class="fas fa-user-plus me-1"></i> Converted Lead
                      </span>
                      <span v-else-if="selectedActiveJob.contract_id" class="aj-trigger-badge aj-trigger-badge--contract">
                        <i class="fas fa-file-signature me-1"></i> Executed Contract
                      </span>
                      <span v-else class="aj-trigger-badge aj-trigger-badge--manual">
                        <i class="fas fa-hand-pointer me-1"></i> Manual Staging
                      </span>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-md-4">
                  <div class="aj-card-meta">
                    <div class="d-flex justify-content-between align-items-center">
                      <span class="aj-card-label">Progress & Tasks</span>
                      <span class="aj-progress-text">{{ calculateProgressPercent(selectedActiveJob) }}%</span>
                    </div>
                    <div class="mt-1">
                      <div class="aj-progress-track">
                        <div class="aj-progress-bar" :style="{ width: calculateProgressPercent(selectedActiveJob) + '%' }"></div>
                      </div>
                      <span class="aj-progress-sub">{{ selectedActiveJob.completed_tasks_count || 0 }} / {{ selectedActiveJob.tasks_count || 0 }} Completed</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Tasks Hierarchy Section -->
              <div class="mb-4">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div class="d-flex align-items-center gap-2">
                    <div class="aj-section-icon"><i class="fas fa-list-check"></i></div>
                    <h6 class="aj-section-title mb-0">Operational Tasks Hierarchy</h6>
                  </div>
                  <span class="aj-count-pill">{{ (selectedActiveJob.tasks && selectedActiveJob.tasks.length) || 0 }} Top-Level Tasks</span>
                </div>

                <div v-if="!selectedActiveJob.tasks || !selectedActiveJob.tasks.length" class="aj-empty-tasks">
                  <i class="fas fa-inbox fa-2x mb-2 opacity-50"></i>
                  <p class="mb-0 small text-muted">No operational tasks registered for this job.</p>
                </div>

                <div v-else class="aj-tasks-tree d-flex flex-column gap-2">
                  <div
                    v-for="(task, idx) in selectedActiveJob.tasks"
                    :key="task.id"
                    class="aj-task-row"
                  >
                    <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div class="d-flex align-items-start gap-3">
                        <div class="aj-step-circle">#{{ idx + 1 }}</div>
                        <div>
                          <h6 class="aj-task-heading">{{ task.title }}</h6>
                          <p v-if="task.description" class="aj-task-subdesc">{{ task.description }}</p>
                          <div class="aj-tags-cluster">
                            <span class="aj-tag aj-tag--neutral">
                              <i class="fas fa-layer-group me-1"></i> {{ task.hierarchy_level || 'Task' }}
                            </span>
                            <span class="aj-tag aj-tag--neutral">
                              <i class="fas me-1" :class="task.execution_mode === 'Concurrent' ? 'fa-arrows-split-up-and-left' : 'fa-arrow-down-short-wide'"></i> {{ task.execution_mode || 'Sequential' }}
                            </span>
                            <span v-if="task.assigned_role" class="aj-tag aj-tag--role">
                              <i class="fas fa-user-gear me-1"></i> {{ task.assigned_role }}
                            </span>
                            <span v-if="task.is_approval_gate" class="aj-tag aj-tag--gate">
                              <i class="fas fa-shield-halved me-1"></i> Approval Gate
                            </span>
                            <span v-if="task.integration_target && task.integration_target !== 'None'" class="aj-tag aj-tag--integration">
                              <i class="fas fa-plug me-1"></i> {{ task.integration_target }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <span class="aj-status-pill" :class="'aj-status-pill--' + (task.task_status || 'planned').toLowerCase().replace(' ', '')">
                          {{ task.task_status || 'Planned' }}
                        </span>
                      </div>
                    </div>

                    <!-- Subtasks Hierarchy -->
                    <div v-if="task.sub_tasks && task.sub_tasks.length" class="aj-subtasks-branch">
                      <div
                        v-for="(sub, sIdx) in task.sub_tasks"
                        :key="sub.id"
                        class="aj-subtask-pill"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <span class="aj-substep-badge">#{{ idx + 1 }}.{{ sIdx + 1 }}</span>
                          <span class="aj-subtask-title">{{ sub.title }}</span>
                          <span v-if="sub.assigned_role" class="aj-subtask-role">• {{ sub.assigned_role }}</span>
                        </div>
                        <span class="aj-status-pill aj-status-pill--sm" :class="'aj-status-pill--' + (sub.task_status || 'planned').toLowerCase().replace(' ', '')">
                          {{ sub.task_status || 'Planned' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Activity Audit Logs Section -->
              <div v-if="selectedActiveJob.activity_logs && selectedActiveJob.activity_logs.length" class="aj-audit-section mt-4">
                <div class="d-flex align-items-center gap-2 mb-3">
                  <div class="aj-section-icon"><i class="fas fa-history"></i></div>
                  <h6 class="aj-section-title mb-0">Audit Activity Logs</h6>
                </div>
                <div class="aj-audit-list">
                  <div
                    v-for="log in selectedActiveJob.activity_logs"
                    :key="log.id"
                    class="aj-audit-item"
                  >
                    <div class="d-flex align-items-center gap-2">
                      <span class="aj-actor-chip" :class="'aj-actor-chip--' + (log.actor_type || 'user').toLowerCase()">
                        {{ log.actor_type }}
                      </span>
                      <strong class="aj-audit-action">{{ log.action_type }}</strong>
                      <span v-if="log.reason" class="aj-audit-reason text-muted">({{ log.reason }})</span>
                    </div>
                    <span class="aj-audit-date">{{ formatDate(log.created_at) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="pp-modal__divider"></div>
          <div class="pp-modal__actions p-3">
            <button type="button" class="pp-btn pp-btn--ghost" @click="closeActiveJobModal">Close</button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ── View Blueprint Details Modal (Organisation Inspiration Standard) ── -->
    <Transition name="pp-modal-fade">
      <div v-if="showViewBlueprintModal" class="pp-modal-overlay" @click.self="closeViewBlueprintModal">
        <div class="pp-modal pp-modal--wide" style="max-width: 950px;">
          <div class="pp-modal__header d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-3">
              <div class="aj-modal-header-icon">
                <i class="fas fa-project-diagram"></i>
              </div>
              <div>
                <h5 class="aj-modal-title">{{ selectedViewBlueprint?.title || 'Job Blueprint Details' }}</h5>
                <div class="d-flex align-items-center gap-2 flex-wrap small text-muted mt-1">
                  <span v-if="getAuthItemCode(selectedViewBlueprint)" class="aj-code-tag">
                    <i class="fas fa-barcode me-1"></i> {{ getAuthItemCode(selectedViewBlueprint) }}
                  </span>
                  <span class="aj-header-sep">•</span>
                  <span class="aj-badge-status" :class="selectedViewBlueprint?.is_active ? 'aj-badge-status--completed' : 'aj-badge-status--cancelled'">
                    <i class="fas" :class="selectedViewBlueprint?.is_active ? 'fa-check-circle me-1' : 'fa-pause-circle me-1'"></i>
                    {{ selectedViewBlueprint?.is_active ? 'Active Blueprint' : 'Inactive' }}
                  </span>
                  <span class="aj-header-sep">•</span>
                  <span class="aj-header-meta">
                    <i class="fas fa-clock me-1"></i> Created {{ formatDate(selectedViewBlueprint?.created_at) }}
                  </span>
                </div>
              </div>
            </div>
            <button type="button" class="pp-modal__close" @click="closeViewBlueprintModal">&times;</button>
          </div>
          <div class="pp-modal__divider"></div>

          <div class="pp-modal__body p-4" style="max-height: 72vh; overflow-y: auto;">
            <div v-if="loadingViewBlueprintDetails" class="text-center py-5">
              <i class="fas fa-circle-notch fa-spin fa-2x text-muted mb-2"></i>
              <p class="text-muted small">Loading blueprint structure and task templates...</p>
            </div>

            <div v-else-if="selectedViewBlueprint">
              <!-- Top Blueprint Meta Cards -->
              <div class="row g-3 mb-4">
                <div class="col-12 col-md-4">
                  <div class="aj-card-meta">
                    <span class="aj-card-label">Associated Product / Service</span>
                    <div class="mt-1">
                      <strong class="aj-card-val d-block text-truncate" :title="getBlueprintProductName(selectedViewBlueprint)">
                        {{ getBlueprintProductName(selectedViewBlueprint) }}
                      </strong>
                      <span class="text-muted small d-block mt-1">
                        {{ selectedViewBlueprint.product?.category_name || 'Standard' }} • {{ formatCurrency(selectedViewBlueprint.product?.price) }}
                      </span>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-md-4">
                  <div class="aj-card-meta">
                    <span class="aj-card-label">Task Architecture</span>
                    <div class="mt-1">
                      <strong class="aj-card-val d-block text-dark">
                        {{ totalTasksInTree(selectedViewBlueprint) }} Tasks Configured
                      </strong>
                      <div class="d-flex align-items-center gap-1 mt-1 flex-wrap">
                        <span class="pp-seq-badge">
                          <i class="fas fa-arrow-down-1-9 me-1"></i> {{ countSequential(selectedViewBlueprint) }} Sequential
                        </span>
                        <span class="pp-conc-badge">
                          <i class="fas fa-arrows-split-up-and-left me-1"></i> {{ countConcurrent(selectedViewBlueprint) }} Concurrent
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-12 col-md-4">
                  <div class="aj-card-meta">
                    <span class="aj-card-label">Automated Trigger</span>
                    <div class="mt-1">
                      <span class="aj-trigger-badge aj-trigger-badge--lead">
                        <i class="fas fa-bolt me-1"></i> Lead / Won Deal Converted
                      </span>
                      <span class="text-muted small d-block mt-1" style="font-size: 0.75rem;">
                        Automatically stages Active Job & task records upon conversion
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Description if present -->
              <div v-if="selectedViewBlueprint.description" class="p-3 bg-light rounded-3 border mb-4 text-muted small" style="line-height: 1.5; border-color: #e2e8f0 !important;">
                <i class="fas fa-circle-info text-info me-2"></i>
                <span>{{ selectedViewBlueprint.description }}</span>
              </div>

              <!-- Blueprint Tasks Hierarchy Section -->
              <div class="mb-2">
                <div class="d-flex justify-content-between align-items-center mb-3">
                  <div class="d-flex align-items-center gap-2">
                    <div class="aj-section-icon"><i class="fas fa-list-check"></i></div>
                    <h6 class="aj-section-title mb-0">Blueprint Tasks & Operational Hierarchy</h6>
                  </div>
                  <span class="aj-count-pill">{{ (selectedViewBlueprint.tasks && selectedViewBlueprint.tasks.length) || 0 }} Top-Level Phases</span>
                </div>

                <div v-if="!selectedViewBlueprint.tasks || !selectedViewBlueprint.tasks.length" class="aj-empty-tasks p-4 bg-light rounded text-center text-muted">
                  <i class="fas fa-inbox fa-2x mb-2 opacity-50"></i>
                  <p class="mb-0 small">No tasks registered for this blueprint.</p>
                </div>

                <div v-else class="aj-tasks-tree d-flex flex-column gap-2">
                  <div
                    v-for="(task, idx) in selectedViewBlueprint.tasks"
                    :key="task.id || task.uuid"
                    class="aj-task-row"
                  >
                    <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                      <div class="d-flex align-items-start gap-3">
                        <div class="aj-step-circle">#{{ idx + 1 }}</div>
                        <div>
                          <h6 class="aj-task-heading">{{ task.title }}</h6>
                          <p v-if="task.description" class="aj-task-subdesc">{{ task.description }}</p>
                          <div class="aj-tags-cluster">
                            <span class="aj-tag aj-tag--neutral">
                              <i class="fas fa-layer-group me-1"></i> {{ task.hierarchy_level || 'Task' }}
                            </span>
                            <span class="aj-tag aj-tag--neutral">
                              <i class="fas me-1" :class="task.execution_mode === 'Concurrent' ? 'fa-arrows-split-up-and-left' : 'fa-arrow-down-short-wide'"></i> {{ task.execution_mode || 'Sequential' }}
                            </span>
                            <span v-if="task.dynamic_role || task.assigned_role" class="aj-tag aj-tag--role">
                              <i class="fas fa-user-gear me-1"></i> {{ task.dynamic_role || task.assigned_role }}
                            </span>
                            <span v-if="task.is_approval_gate" class="aj-tag aj-tag--gate">
                              <i class="fas fa-shield-halved me-1"></i> Approval Gate
                            </span>
                            <span v-if="task.integration_target && task.integration_target !== 'None'" class="aj-tag aj-tag--integration">
                              <i class="fas fa-plug me-1"></i> {{ task.integration_target }}
                            </span>
                            <span v-if="task.frequency && task.frequency !== 'One-off'" class="aj-tag aj-tag--neutral">
                              <i class="fas fa-repeat me-1"></i> {{ task.frequency }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <span class="aj-status-pill aj-status-pill--planned">
                          Step {{ task.sequence_order !== undefined ? task.sequence_order + 1 : idx + 1 }}
                        </span>
                      </div>
                    </div>

                    <!-- Subtasks Hierarchy -->
                    <div v-if="task.sub_tasks && task.sub_tasks.length" class="aj-subtasks-branch">
                      <div
                        v-for="(sub, sIdx) in task.sub_tasks"
                        :key="sub.id || sub.uuid"
                        class="aj-subtask-pill"
                      >
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                          <span class="aj-substep-badge">#{{ idx + 1 }}.{{ sIdx + 1 }}</span>
                          <span class="aj-subtask-title">{{ sub.title }}</span>
                          <span v-if="sub.dynamic_role || sub.assigned_role" class="aj-subtask-role">• {{ sub.dynamic_role || sub.assigned_role }}</span>
                          <span v-if="sub.is_approval_gate" class="badge" style="background: #fffbeb; color: #b45309; border: 1px solid #fde68a; font-size: 0.7rem;">
                            <i class="fas fa-shield-halved me-1"></i> Approval
                          </span>
                        </div>
                        <span class="aj-status-pill aj-status-pill--sm aj-status-pill--planned">
                          {{ sub.execution_mode || 'Sequential' }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="pp-modal__divider"></div>
          <div class="pp-modal__actions p-3 d-flex justify-content-between align-items-center">
            <div>
              <button
                type="button"
                class="btn btn-outline-secondary btn-sm d-inline-flex align-items-center gap-1"
                @click="cloneFromViewModal"
              >
                <i class="fa-solid fa-clone"></i> Clone Blueprint
              </button>
            </div>
            <div class="d-flex align-items-center gap-2">
              <button type="button" class="btn btn-secondary btn-sm" @click="closeViewBlueprintModal">Close</button>
              <button
                type="button"
                class="btn btn-primary btn-sm d-inline-flex align-items-center gap-1"
                style="background-color: #00A0C1; border-color: #00A0C1; color: #fff; font-weight: 600;"
                @click="editFromViewModal"
              >
                <img src="@/assets/img/icons/universal_edit.svg" alt="Edit" style="width: 16px; height: 16px; filter: brightness(0) invert(1);" />
                Edit Blueprint
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ── Blueprint Editor Modal Overlay ── -->
    <Transition name="pp-modal-fade">
      <div v-if="showEditorModal" class="pp-modal-overlay" @click.self="closeEditorModal">
        <div class="pp-modal pp-modal--wide">
          <JobBlueprintEditor
            :blueprint-id="editingBlueprintId"
            :initial-product-id="selectedInitialProductId"
            :clone-from-id="cloningFromId"
            :is-modal="true"
            @saved="handleBlueprintSaved"
            @close="closeEditorModal"
          />
        </div>
      </div>
    </Transition>

    <!-- ── Product Blueprints List Modal (Multi-Blueprint Product View) ── -->
    <Transition name="pp-modal-fade">
      <div v-if="showProductBlueprintsModal" class="pp-modal-overlay" @click.self="closeProductBlueprintsModal">
        <div class="pp-modal pp-modal--wide" style="max-width: 880px;">
          <div class="pp-modal__header d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center gap-3">
              <div class="aj-modal-header-icon">
                <i class="fas fa-boxes-stacked"></i>
              </div>
              <div>
                <h5 class="aj-modal-title">
                  {{ selectedProductForBlueprints?.display_name || selectedProductForBlueprints?.product_name || 'Product Blueprints' }}
                </h5>
                <div class="d-flex align-items-center gap-2 flex-wrap small text-muted mt-1">
                  <span v-if="getAuthItemCode(selectedProductForBlueprints)" class="aj-code-tag">
                    <i class="fas fa-barcode me-1"></i> {{ getAuthItemCode(selectedProductForBlueprints) }}
                  </span>
                  <span class="aj-header-sep">•</span>
                  <span class="aj-header-meta">
                    {{ selectedProductForBlueprints?.category_name || 'Standard' }} ({{ formatCurrency(selectedProductForBlueprints?.price) }})
                  </span>
                  <span class="aj-header-sep">•</span>
                  <span class="aj-badge-status aj-badge-status--completed">
                    <i class="fas fa-check-circle me-1"></i> {{ selectedProductBlueprints.length }} Blueprints Configured
                  </span>
                </div>
              </div>
            </div>
            <button type="button" class="pp-modal__close" @click="closeProductBlueprintsModal">&times;</button>
          </div>
          <div class="pp-modal__divider"></div>

          <div class="pp-modal__body p-4" style="max-height: 70vh; overflow-y: auto;">
            <div v-if="!selectedProductBlueprints.length" class="text-center py-4 text-muted">
              <i class="fas fa-project-diagram fa-2x mb-2 opacity-50"></i>
              <p class="mb-0 small">No blueprints found for this product.</p>
            </div>

            <div v-else class="d-flex flex-column gap-3">
              <div
                v-for="bp in selectedProductBlueprints"
                :key="bp.uuid || bp.id"
                class="aj-task-row d-flex justify-content-between align-items-center flex-wrap gap-3"
              >
                <div class="d-flex align-items-start gap-3">
                  <div
                    class="pp-bp-icon"
                    style="cursor: pointer;"
                    title="View Blueprint Details"
                    @click="closeProductBlueprintsModal(); openViewBlueprintModal(bp.uuid || bp.id)"
                  >
                    <i class="fas fa-project-diagram"></i>
                  </div>
                  <div>
                    <div class="d-flex align-items-center gap-2 flex-wrap">
                      <a
                        href="#"
                        class="aj-task-heading text-decoration-none mb-0"
                        style="color: #00363f; cursor: pointer;"
                        @click.prevent="closeProductBlueprintsModal(); openViewBlueprintModal(bp.uuid || bp.id)"
                      >
                        {{ bp.title }}
                      </a>
                      <span v-if="bp.is_active !== false" class="aj-badge-status aj-badge-status--completed">
                        <i class="fas fa-check me-1"></i> Active
                      </span>
                      <span v-else class="aj-badge-status aj-badge-status--cancelled">
                        <i class="fas fa-pause me-1"></i> Inactive
                      </span>
                    </div>
                    <p v-if="bp.description" class="aj-task-subdesc mt-1 mb-2">{{ bp.description }}</p>
                    <div class="d-flex align-items-center gap-2 mt-1 flex-wrap">
                      <span class="pp-task-count-badge">
                        <i class="fas fa-list-check me-1"></i>
                        {{ bp.tasks_count !== undefined ? bp.tasks_count : (bp.tasks ? bp.tasks.length : 0) }} Tasks
                      </span>
                      <span class="pp-seq-badge">
                        <i class="fas fa-arrow-down-1-9 me-1"></i> {{ countSequential(bp) }} Seq
                      </span>
                      <span class="pp-conc-badge">
                        <i class="fas fa-arrows-split-up-and-left me-1"></i> {{ countConcurrent(bp) }} Conc
                      </span>
                    </div>
                  </div>
                </div>

                <div class="action-button-container">
                  <button
                    type="button"
                    class="btn-svg-icon"
                    title="View Blueprint Details"
                    @click="closeProductBlueprintsModal(); openViewBlueprintModal(bp.uuid || bp.id)"
                  >
                    <i class="fa-solid fa-eye icon-svg-fa"></i>
                  </button>
                  <button
                    type="button"
                    class="btn-svg-icon"
                    title="Edit Blueprint"
                    @click="closeProductBlueprintsModal(); openEditModal(bp.uuid || bp.id)"
                  >
                    <img src="@/assets/img/icons/universal_edit.svg" alt="Edit" class="icon-svg" />
                  </button>
                  <button
                    type="button"
                    class="btn-svg-icon"
                    title="Clone / Duplicate Blueprint"
                    @click="closeProductBlueprintsModal(); cloneBlueprint(bp.uuid || bp.id)"
                  >
                    <i class="fa-solid fa-clone icon-svg-fa"></i>
                  </button>
                  <button
                    type="button"
                    class="btn-svg-icon btn-svg-icon--delete"
                    title="Delete Blueprint"
                    @click="closeProductBlueprintsModal(); confirmDelete(bp)"
                  >
                    <img src="@/assets/img/icons/universal_delete.svg" alt="Delete" class="icon-svg" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="pp-modal__divider"></div>
          <div class="pp-modal__actions p-3 d-flex justify-content-between align-items-center">
            <button
              type="button"
              class="pp-btn pp-btn--primary pp-btn--sm"
              @click="
                const prodId = selectedProductForBlueprints?.product_id || selectedProductForBlueprints?.id;
                closeProductBlueprintsModal();
                openCreateModal(prodId);
              "
            >
              <i class="fas fa-plus me-1"></i> Add Another Blueprint
            </button>
            <button type="button" class="pp-btn pp-btn--ghost pp-btn--sm" @click="closeProductBlueprintsModal">
              Close
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- ── Delete Confirmation Modal ── -->
    <Transition name="pp-modal-fade">
      <div v-if="showConfirmDelete" class="pp-modal-overlay" @click.self="showConfirmDelete = false">
        <div class="pp-modal pp-modal--sm">
          <div class="pp-modal__header">
            <h5 class="pp-modal__title text-danger">
              <i class="fas fa-triangle-exclamation"></i> Delete Job Blueprint
            </h5>
            <button class="pp-modal__close" @click="showConfirmDelete = false">&times;</button>
          </div>
          <div class="pp-modal__divider"></div>
          <div class="pp-modal__body">
            <p class="mb-2">Are you sure you want to delete blueprint <strong>{{ blueprintToDelete?.title }}</strong>?</p>
            <p class="text-muted small mb-0">This will soft-delete the blueprint and its associated task templates.</p>
          </div>
          <div class="pp-modal__actions">
            <button type="button" class="pp-btn pp-btn--ghost" @click="showConfirmDelete = false">Cancel</button>
            <button type="button" class="pp-btn pp-btn--danger" @click="executeDelete">
              <i class="fas fa-trash-alt me-1"></i> Delete Blueprint
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import JobBlueprintEditor from "./JobBlueprintEditor.vue";

export default {
  name: "JobsCatalogueDashboard",
  components: {
    JobBlueprintEditor,
  },
  data() {
    return {
      loading: false,
      activeTab: "products", // 'products' | 'blueprints' | 'active_jobs'

      products: [],
      blueprints: [],
      activeJobs: [],

      searchQuery: "",
      productStatusFilter: "all", // 'all' | 'linked' | 'unconfigured'
      blueprintStatusFilter: "all", // 'all' | 'active' | 'inactive'
      activeJobStatusFilter: "all", // 'all' | 'Pending' | 'In Progress' | 'Completed' | 'Cancelled'

      productPage: 1,
      blueprintPage: 1,
      activeJobsPage: 1,
      pageSize: 15,
      rowsPerPageList: [
        { text: "10", value: 10 },
        { text: "15", value: 15 },
        { text: "25", value: 25 },
        { text: "50", value: 50 },
        { text: "100", value: 100 },
      ],

      // Modal State
      showEditorModal: false,
      editingBlueprintId: "",
      selectedInitialProductId: "",
      cloningFromId: "",

      // Deletion confirmation
      showConfirmDelete: false,
      blueprintToDelete: null,

      // Active Job Details Modal
      showActiveJobModal: false,
      selectedActiveJob: null,
      loadingActiveJobDetails: false,

      // View Blueprint Details Modal
      showViewBlueprintModal: false,
      selectedViewBlueprint: null,
      loadingViewBlueprintDetails: false,

      // Product Blueprints List Modal (Multi-Blueprint Product View)
      showProductBlueprintsModal: false,
      selectedProductForBlueprints: null,
      selectedProductBlueprints: [],
    };
  },
  computed: {
    linkedProductsCount() {
      return this.products.filter((p) => p.has_blueprint).length;
    },
    unconfiguredProductsCount() {
      return this.products.filter((p) => !p.has_blueprint).length;
    },
    coveragePercentage() {
      if (!this.products.length) return 0;
      return Math.round((this.linkedProductsCount / this.products.length) * 100);
    },
    totalTasksCount() {
      return this.blueprints.reduce((sum, bp) => {
        const count = bp.tasks_count !== undefined ? bp.tasks_count : (bp.tasks ? bp.tasks.length : 0);
        return sum + Number(count || 0);
      }, 0);
    },
    filteredProducts() {
      let list = [...this.products];

      if (this.productStatusFilter === "linked") {
        list = list.filter((p) => p.has_blueprint);
      } else if (this.productStatusFilter === "unconfigured") {
        list = list.filter((p) => !p.has_blueprint);
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(
          (p) =>
            (p.display_name && p.display_name.toLowerCase().includes(q)) ||
            (p.product_name && p.product_name.toLowerCase().includes(q)) ||
            (this.getAuthItemCode(p) && this.getAuthItemCode(p).toLowerCase().includes(q)) ||
            (p.blueprint_title && p.blueprint_title.toLowerCase().includes(q))
        );
      }

      return list;
    },
    paginatedProducts() {
      const start = (this.productPage - 1) * this.pageSize;
      return this.filteredProducts.slice(start, start + this.pageSize);
    },
    filteredBlueprints() {
      let list = [...this.blueprints];

      if (this.blueprintStatusFilter === "active") {
        list = list.filter((bp) => bp.is_active);
      } else if (this.blueprintStatusFilter === "inactive") {
        list = list.filter((bp) => !bp.is_active);
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(
          (bp) =>
            (bp.title && bp.title.toLowerCase().includes(q)) ||
            (this.getAuthItemCode(bp) && this.getAuthItemCode(bp).toLowerCase().includes(q)) ||
            (bp.description && bp.description.toLowerCase().includes(q)) ||
            (bp.product && bp.product.display_name && bp.product.display_name.toLowerCase().includes(q))
        );
      }

      return list;
    },
    paginatedBlueprints() {
      const start = (this.blueprintPage - 1) * this.pageSize;
      return this.filteredBlueprints.slice(start, start + this.pageSize);
    },
    filteredActiveJobs() {
      let list = [...this.activeJobs];

      if (this.activeJobStatusFilter && this.activeJobStatusFilter !== "all") {
        list = list.filter((job) => job.job_status === this.activeJobStatusFilter);
      }

      if (this.searchQuery) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(
          (job) =>
            (job.title && job.title.toLowerCase().includes(q)) ||
            (job.organisation_name && job.organisation_name.toLowerCase().includes(q)) ||
            (job.lead_title && job.lead_title.toLowerCase().includes(q)) ||
            (job.blueprint && job.blueprint.title && job.blueprint.title.toLowerCase().includes(q)) ||
            (job.health_indicator && job.health_indicator.toLowerCase().includes(q)) ||
            (job.job_status && job.job_status.toLowerCase().includes(q))
        );
      }

      return list;
    },
    paginatedActiveJobs() {
      const start = (this.activeJobsPage - 1) * this.pageSize;
      return this.filteredActiveJobs.slice(start, start + this.pageSize);
    },
    currentTotalItems() {
      if (this.activeTab === "products") return this.filteredProducts.length;
      if (this.activeTab === "blueprints") return this.filteredBlueprints.length;
      if (this.activeTab === "active_jobs") return this.filteredActiveJobs.length;
      return 0;
    },
    currentPage() {
      if (this.activeTab === "products") return this.productPage;
      if (this.activeTab === "blueprints") return this.blueprintPage;
      if (this.activeTab === "active_jobs") return this.activeJobsPage;
      return 1;
    },
    currentTotalPages() {
      return Math.ceil(this.currentTotalItems / this.pageSize) || 1;
    },
    currentPaginationLabel() {
      if (this.currentTotalItems === 0) return "0";
      const start = (this.currentPage - 1) * this.pageSize + 1;
      const end = Math.min(this.currentPage * this.pageSize, this.currentTotalItems);
      return `${start}-${end}`;
    },
  },
  watch: {
    searchQuery() {
      this.productPage = 1;
      this.blueprintPage = 1;
      this.activeJobsPage = 1;
    },
    productStatusFilter() {
      this.productPage = 1;
    },
    blueprintStatusFilter() {
      this.blueprintPage = 1;
    },
    activeJobStatusFilter() {
      this.activeJobsPage = 1;
    },
    pageSize() {
      this.productPage = 1;
      this.blueprintPage = 1;
      this.activeJobsPage = 1;
    },
  },
  created() {
    this.checkSuperAdminAccess();
    if (this.$route.query.tab) {
      this.activeTab = this.$route.query.tab;
    }
    this.fetchData();
  },
  methods: {
    previousPage() {
      if (this.currentPage > 1) {
        if (this.activeTab === "products") {
          this.productPage--;
        } else if (this.activeTab === "blueprints") {
          this.blueprintPage--;
        } else if (this.activeTab === "active_jobs") {
          this.activeJobsPage--;
        }
      }
    },
    nextPage() {
      if (this.currentPage < this.currentTotalPages) {
        if (this.activeTab === "products") {
          this.productPage++;
        } else if (this.activeTab === "blueprints") {
          this.blueprintPage++;
        } else if (this.activeTab === "active_jobs") {
          this.activeJobsPage++;
        }
      }
    },
    checkSuperAdminAccess() {
      const isSuperAdmin = this.$store.state.auth.isSuperAdmin;
      const isAdmin = this.$store.state.auth.isAdmin;
      if (!isSuperAdmin && !isAdmin) {
        this.$toast.error("Unauthorized! Only Super Admins and Admins can access the Jobs Catalogue.");
        this.$router.push("/dashboard");
      }
    },
    async fetchData() {
      this.loading = true;
      try {
        await Promise.all([this.fetchProducts(), this.fetchBlueprints(), this.fetchActiveJobs()]);
      } catch (err) {
        console.error("Failed to load catalogue data:", err);
        this.$toast.error("Failed to load Jobs Catalogue data.");
      } finally {
        this.loading = false;
      }
    },
    async refreshData() {
      await this.fetchData();
      this.$toast.success("Jobs Catalogue refreshed!");
    },
    async fetchProducts() {
      const resp = await this.$axios.get("/api/v1/jobs/blueprints/products");
      if (resp.data && resp.data.data) {
        this.products = resp.data.data;
      }
    },
    async fetchBlueprints() {
      const resp = await this.$axios.get("/api/v1/jobs/blueprints");
      if (resp.data && resp.data.data) {
        this.blueprints = resp.data.data;
      }
    },
    async fetchActiveJobs() {
      try {
        const resp = await this.$axios.get("/api/v1/jobs/active");
        if (resp.data && resp.data.data) {
          this.activeJobs = resp.data.data;
        }
      } catch (err) {
        console.error("Failed to fetch active jobs:", err);
      }
    },
    async openActiveJobModal(jobId) {
      this.showActiveJobModal = true;
      this.loadingActiveJobDetails = true;
      this.selectedActiveJob = null;
      try {
        const resp = await this.$axios.get(`/api/v1/jobs/active/${jobId}`);
        if (resp.data && resp.data.data) {
          this.selectedActiveJob = resp.data.data;
        }
      } catch (err) {
        console.error("Failed to fetch active job details:", err);
        this.$toast.error("Failed to load job details.");
      } finally {
        this.loadingActiveJobDetails = false;
      }
    },
    closeActiveJobModal() {
      this.showActiveJobModal = false;
      this.selectedActiveJob = null;
    },
    calculateProgressPercent(job) {
      if (!job || !job.tasks_count || job.tasks_count === 0) return 0;
      const completed = job.completed_tasks_count || 0;
      return Math.round((completed / job.tasks_count) * 100);
    },
    getJobStatusBadgeClass(status) {
      switch (status) {
        case "Completed":
          return "bg-success text-white";
        case "In Progress":
          return "bg-info text-white";
        case "Cancelled":
          return "bg-danger text-white";
        case "Pending":
        default:
          return "bg-warning text-dark";
      }
    },
    getJobHealthBadgeClass(health) {
      switch (health) {
        case "Behind Plan":
          return "bg-warning text-dark";
        case "Overdue":
          return "bg-danger text-white";
        case "On Track":
        default:
          return "bg-success text-white";
      }
    },
    getTaskStatusBadgeClass(status) {
      switch (status) {
        case "Completed":
          return "bg-success text-white";
        case "InProgress":
        case "In Progress":
          return "bg-primary text-white";
        case "Initiated":
          return "bg-info text-white";
        case "Planned":
        default:
          return "bg-light text-secondary border";
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return "-";
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString("en-AU", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      } catch (e) {
        return dateStr;
      }
    },
    openCreateModal(productId = "") {
      this.editingBlueprintId = "";
      this.cloningFromId = "";
      this.selectedInitialProductId = productId;
      this.showEditorModal = true;
    },
    openEditModal(blueprintUuid) {
      if (!blueprintUuid) return;
      this.selectedInitialProductId = "";
      this.cloningFromId = "";
      this.editingBlueprintId = blueprintUuid;
      this.showEditorModal = true;
    },
    cloneBlueprint(blueprintUuid) {
      this.selectedInitialProductId = "";
      this.editingBlueprintId = "";
      this.cloningFromId = blueprintUuid;
      this.showEditorModal = true;
    },
    closeEditorModal() {
      this.showEditorModal = false;
      this.editingBlueprintId = "";
      this.selectedInitialProductId = "";
      this.cloningFromId = "";
    },
    handleBlueprintSaved() {
      this.closeEditorModal();
      this.fetchData();
    },
    confirmDelete(bp) {
      this.blueprintToDelete = bp;
      this.showConfirmDelete = true;
    },
    async executeDelete() {
      if (!this.blueprintToDelete) return;
      const bpId = this.blueprintToDelete.uuid || this.blueprintToDelete.id;
      try {
        await this.$axios.delete(`/api/v1/jobs/blueprints/${bpId}`);
        this.$toast.success("Blueprint deleted successfully!");
        this.showConfirmDelete = false;
        this.blueprintToDelete = null;
        this.fetchData();
      } catch (err) {
        console.error("Delete blueprint error:", err);
        this.$toast.error("Failed to delete blueprint.");
      }
    },
    countSequential(bp) {
      if (!bp.tasks || !bp.tasks.length) return 0;
      let count = 0;
      const countSeq = (tasks) => {
        tasks.forEach((t) => {
          if (t.execution_mode === "Sequential" || !t.execution_mode) count++;
          if (t.sub_tasks && t.sub_tasks.length) countSeq(t.sub_tasks);
        });
      };
      countSeq(bp.tasks);
      return count;
    },
    countConcurrent(bp) {
      if (!bp.tasks || !bp.tasks.length) return 0;
      let count = 0;
      const countConc = (tasks) => {
        tasks.forEach((t) => {
          if (t.execution_mode === "Concurrent") count++;
          if (t.sub_tasks && t.sub_tasks.length) countConc(t.sub_tasks);
        });
      };
      countConc(bp.tasks);
      return count;
    },
    formatCurrency(val) {
      if (val === undefined || val === null || val === "") return "$0.00";
      const num = Number(val);
      if (isNaN(num)) return val;
      return `$${num.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    isUUID(uuid) {
      if (typeof uuid !== "string") return false;
      if (uuid.length !== 36) return false;
      if (uuid[8] !== "-" || uuid[13] !== "-" || uuid[18] !== "-" || uuid[23] !== "-") return false;
      return true;
    },
    getAuthItemCode(item) {
      if (!item) return null;
      if (item.auth_item_code && !this.isUUID(item.auth_item_code)) {
        return item.auth_item_code;
      }
      if (item.product_id && !this.isUUID(item.product_id)) {
        return item.product_id;
      }
      if (item.id && !this.isUUID(item.id)) {
        return item.id;
      }
      if (item.product_name && !this.isUUID(item.product_name)) {
        return item.product_name;
      }
      if (item.product?.uuid && !this.isUUID(item.product.uuid)) {
        return item.product.uuid;
      }
      if (item.product?.name && !this.isUUID(item.product.name)) {
        return item.product.name;
      }
      return null;
    },
    getBlueprintsForProduct(prod) {
      if (!prod) return [];
      const pCode = this.getAuthItemCode(prod);
      const pId = prod.product_id || prod.id;
      const pName = (prod.name || prod.product_name || "").toLowerCase().trim();
      const pDisplay = (prod.display_name || "").toLowerCase().trim();

      return this.blueprints.filter((bp) => {
        if (!bp) return false;
        const bpId = bp.uuid || bp.id;
        if (prod.blueprint_uuid && (bpId === prod.blueprint_uuid || bp.uuid === prod.blueprint_uuid)) return true;
        if (pId && (bp.product_id === pId || bp.product?.id === pId || bp.product?.uuid === pId)) return true;
        const bpCode = this.getAuthItemCode(bp) || bp.auth_item_code;
        if (pCode && bpCode && pCode.toLowerCase() === bpCode.toLowerCase()) return true;
        if (pName && (bp.product_id?.toLowerCase() === pName || bp.auth_item_code?.toLowerCase() === pName)) return true;
        if (pDisplay && (bp.product_id?.toLowerCase() === pDisplay || bp.auth_item_code?.toLowerCase() === pDisplay)) return true;
        if (bp.title && (pDisplay || pName)) {
          const bpTitle = bp.title.toLowerCase();
          if (pDisplay && (bpTitle.includes(pDisplay) || pDisplay.includes(bpTitle))) return true;
          if (pName && (bpTitle.includes(pName) || pName.includes(bpTitle))) return true;
        }
        return false;
      });
    },
    isProductConfigured(prod) {
      if (!prod) return false;
      if (prod.has_blueprint) return true;
      return this.getBlueprintsForProduct(prod).length > 0;
    },
    getProductBlueprintsCount(prod) {
      if (!prod) return 0;
      const matchedCount = this.getBlueprintsForProduct(prod).length;
      if (prod.blueprints && Array.isArray(prod.blueprints)) {
        return Math.max(prod.blueprints.length, matchedCount, prod.blueprints_count || 0);
      }
      if (prod.blueprints_count && prod.blueprints_count > 0) {
        return Math.max(prod.blueprints_count, matchedCount);
      }
      return matchedCount;
    },
    getProductBlueprintTitle(prod) {
      if (!prod) return "";
      const count = this.getProductBlueprintsCount(prod);
      const matches = this.getBlueprintsForProduct(prod);
      const primaryTitle =
        prod.blueprint_title ||
        (matches.length > 0 && matches[0].title ? matches[0].title : "") ||
        (prod.blueprints && prod.blueprints.length > 0 && prod.blueprints[0].title ? prod.blueprints[0].title : "");
      if (count > 1 && primaryTitle) {
        return `${primaryTitle} (+${count - 1} more)`;
      }
      return primaryTitle || "View Blueprint";
    },
    openProductBlueprintsModal(prod) {
      if (!prod) return;
      const matches = this.getBlueprintsForProduct(prod);
      const list =
        matches.length > 0
          ? matches
          : Array.isArray(prod.blueprints)
          ? prod.blueprints
          : [];
      this.selectedProductForBlueprints = prod;
      this.selectedProductBlueprints = list;
      this.showProductBlueprintsModal = true;
    },
    closeProductBlueprintsModal() {
      this.showProductBlueprintsModal = false;
      this.selectedProductForBlueprints = null;
      this.selectedProductBlueprints = [];
    },
    openViewBlueprintForProduct(prod) {
      if (!prod) return;
      const matches = this.getBlueprintsForProduct(prod);
      const hasMultiple =
        (prod.blueprints && prod.blueprints.length > 1) ||
        prod.blueprints_count > 1 ||
        matches.length > 1;

      if (hasMultiple) {
        this.openProductBlueprintsModal(prod);
        return;
      }
      if (prod.blueprint_uuid) {
        this.openViewBlueprintModal(prod.blueprint_uuid);
        return;
      }
      if (matches.length > 0) {
        this.openViewBlueprintModal(matches[0].uuid || matches[0].id);
      } else if (prod.blueprints && prod.blueprints.length === 1) {
        this.openViewBlueprintModal(prod.blueprints[0].uuid || prod.blueprints[0].id);
      } else {
        this.$toast.info("No blueprint configured for this product yet.");
      }
    },
    openEditBlueprintForProduct(prod) {
      if (!prod) return;
      const matches = this.getBlueprintsForProduct(prod);
      const hasMultiple =
        (prod.blueprints && prod.blueprints.length > 1) ||
        prod.blueprints_count > 1 ||
        matches.length > 1;

      if (hasMultiple) {
        this.openProductBlueprintsModal(prod);
        return;
      }
      if (prod.blueprint_uuid) {
        this.openEditModal(prod.blueprint_uuid);
        return;
      }
      if (matches.length > 0) {
        this.openEditModal(matches[0].uuid || matches[0].id);
      } else {
        this.openCreateModal(prod.product_id || prod.id);
      }
    },
    async openViewBlueprintModal(blueprintUuid) {
      if (!blueprintUuid) return;
      this.showViewBlueprintModal = true;
      this.loadingViewBlueprintDetails = true;
      this.selectedViewBlueprint = null;
      try {
        const resp = await this.$axios.get(`/api/v1/jobs/blueprints/${blueprintUuid}`);
        if (resp.data && resp.data.data) {
          this.selectedViewBlueprint = resp.data.data;
        }
      } catch (err) {
        console.error("Failed to load blueprint details:", err);
        this.$toast.error("Failed to load blueprint details.");
      } finally {
        this.loadingViewBlueprintDetails = false;
      }
    },
    closeViewBlueprintModal() {
      this.showViewBlueprintModal = false;
      this.selectedViewBlueprint = null;
    },
    editFromViewModal() {
      const uuid = this.selectedViewBlueprint?.uuid || this.selectedViewBlueprint?.id;
      this.closeViewBlueprintModal();
      if (uuid) {
        this.openEditModal(uuid);
      }
    },
    cloneFromViewModal() {
      const uuid = this.selectedViewBlueprint?.uuid || this.selectedViewBlueprint?.id;
      this.closeViewBlueprintModal();
      if (uuid) {
        this.cloneBlueprint(uuid);
      }
    },
    totalTasksInTree(bp) {
      if (!bp || !bp.tasks) return 0;
      let count = 0;
      const countAll = (tasks) => {
        tasks.forEach((t) => {
          count++;
          if (t.sub_tasks && t.sub_tasks.length) countAll(t.sub_tasks);
        });
      };
      countAll(bp.tasks);
      return count;
    },
    getBlueprintProductName(bp) {
      if (!bp) return "Standard Service";
      if (bp.product) {
        return bp.product.display_name || bp.product.product_name || bp.product.name || "Standard Service";
      }
      if (bp.product_id) {
        const found = this.products.find((p) => p.product_id === bp.product_id);
        if (found) return found.display_name || found.product_name || "Standard Service";
      }
      return "Standard Service";
    },
    getProductName(productId) {
      if (!productId) return "";
      const found = this.products.find((p) => p.product_id === productId);
      return found ? (found.display_name || found.product_name) : "";
    },
  },
};
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════
   JOBS CATALOGUE — Native Spark Design System Standard
   ═══════════════════════════════════════════════════════════ */

/* ── Page Header ── */
.ms-page-header {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
  border: 1px solid rgba(0, 130, 152, 0.15);
  border-left: 5px solid #008298;
  border-radius: 12px;
  box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ms-page-header__icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: rgba(0, 130, 152, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  color: #008298;
}

.ms-page-header__title {
  font-size: 1.45rem;
  font-weight: 800;
  color: #00363f;
  margin: 0;
  letter-spacing: -0.3px;
}

.ms-page-header__subtitle {
  font-size: 0.85rem;
  color: #557d84;
  margin: 4px 0 0;
  font-weight: 400;
}

.ms-page-header__breadcrumb {
  font-size: 0.76rem;
  color: #557d84;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
}

.ms-breadcrumb-sep {
  margin: 0 6px;
  color: rgba(0, 130, 152, 0.3);
}

/* ── Summary Cards ── */
.pp-summary-row {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 16px;
  margin-bottom: 20px;
}

.pp-stat-card {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.pp-stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.pp-stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.pp-stat-body {
  display: flex;
  flex-direction: column;
}

.pp-stat-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.2;
}

.pp-stat-label {
  font-size: 0.8rem;
  color: #94a3b8;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.pp-stat-badge {
  font-size: 0.75rem;
  font-weight: 700;
  color: #059669;
  background: #ecfdf5;
  padding: 2px 6px;
  border-radius: 10px;
}

/* ── Main Card Container ── */
.pp-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

/* ── Tab Navigation (Matching Organisations Standard) ── */
.org-tabs-container {
  background-color: #f8fafc;
  border-bottom: 1.5px solid #e2e8f0;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.org-tabs-nav {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.org-tab-btn {
  background: none;
  border: none;
  padding: 16px 20px;
  font-weight: 600;
  color: #64748b;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  outline: none;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  user-select: none;
}

.org-tab-btn:hover {
  color: #00768a;
}

.org-tab-btn.active {
  color: #00768a;
  font-weight: 700;
}

.org-tab-btn.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 3px;
  background-color: #00768a;
  border-radius: 3px 3px 0 0;
}

.org-tab-counter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1;
  margin-left: 8px;
  background-color: #e2e8f0;
  color: #475569;
  transition: all 0.2s ease;
}

.org-tab-btn.active .org-tab-counter-badge {
  background-color: #00768a;
  color: #ffffff;
}

/* ── Toolbar ── */
.pp-card__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f5f9;
}

.pp-search-wrap {
  position: relative;
  min-width: 280px;
}

.pp-search-input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  color: #334155;
  background: #f8fafc;
  transition: border-color 0.2s, box-shadow 0.2s, background-color 0.2s;
  outline: none;
}

.pp-search-input:focus {
  border-color: #00A0C1;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.12);
  background: #fff;
}

.pp-toolbar-right {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
}

.pp-toolbar-group {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pp-toolbar-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.pp-select {
  padding: 6px 28px 6px 10px;
  border: 1.5px solid #e2e8f0;
  border-radius: 6px;
  font-size: 0.85rem;
  color: #00A0C1;
  font-weight: 600;
  background: #f8fafc;
  cursor: pointer;
  outline: none;
  transition: border-color 0.2s;
  appearance: auto;
}

.pp-select:focus {
  border-color: #00A0C1;
}

/* ── Buttons (Simple Solid Colors - Matching Spark Standards) ── */
.pp-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
  border: 1px solid transparent;
}

.pp-btn--sm {
  padding: 6px 14px;
  font-size: 0.82rem;
  border-radius: 6px;
}

.pp-btn--primary {
  background-color: #00A0C1;
  border-color: #00A0C1;
  color: #ffffff;
  box-shadow: none;
}

.pp-btn--primary:hover {
  background-color: #008298;
  border-color: #008298;
  color: #ffffff;
  transform: none;
  box-shadow: none;
}

.pp-btn--ghost {
  background-color: transparent;
  color: #008298;
  border: 1px solid #cbd5e1;
}

.pp-btn--ghost:hover {
  background-color: #f1f5f9;
  border-color: #94a3b8;
  color: #006070;
}

.pp-btn--danger {
  background-color: #ef4444;
  border-color: #ef4444;
  color: #ffffff;
}

.pp-btn--danger:hover {
  background-color: #dc2626;
  border-color: #dc2626;
}

/* ── Tables ── */
.pp-table-wrap {
  overflow-x: auto;
}

.pp-table {
  width: 100%;
  border-collapse: collapse;
}

.pp-table thead tr {
  border-bottom: 2px solid #e2e8f0;
  background: #f8fafc;
}

.pp-table th {
  padding: 12px 18px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  white-space: nowrap;
}

.pp-table td {
  padding: 14px 18px;
  font-size: 0.875rem;
  color: #334155;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.pp-table tbody tr {
  transition: background-color 0.15s ease;
}

.pp-table tbody tr:hover {
  background-color: #f8fafc;
}

.pp-cell-name {
  font-weight: 600;
  color: #1e293b;
}

.pp-prod-icon, .pp-bp-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #e6f7fa;
  color: #008298;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.pp-product-name {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0f172a;
}

.pp-product-subdesc, .pp-blueprint-subtext {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 400;
  margin-top: 2px;
}

.pp-code-badge {
  font-family: monospace;
  font-size: 0.76rem;
  background: #f1f5f9;
  color: #334155;
  padding: 3px 7px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
}

.pp-category-text {
  font-size: 0.82rem;
  color: #64748b;
  font-weight: 500;
}

.pp-price-badge {
  display: inline-block;
  font-weight: 700;
  color: #0284c7;
  background: #f0f9ff;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 0.82rem;
}

.pp-task-count-badge {
  display: inline-flex;
  align-items: center;
  background: #eef2ff;
  color: #4338ca;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.78rem;
  font-weight: 700;
}

.pp-seq-badge {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 6px;
  background: #f1f5f9;
  color: #475569;
}

.pp-conc-badge {
  font-size: 0.72rem;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 6px;
  background: #fff7ed;
  color: #c2410c;
}

/* ── Badges ── */
.pp-linked-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: #ecfdf5;
  color: #059669;
  border-radius: 16px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

.pp-unlinked-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  background: #fffbeb;
  color: #d97706;
  border-radius: 16px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

/* ── Action Buttons & Container (Modern Standard) ── */
.pp-actions {
  text-align: right;
  white-space: nowrap;
}

.action-button-container {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-wrap: nowrap;
}

.btn-svg-icon {
  position: relative;
  width: 36px;
  height: 36px;
  border-radius: 50% !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: #e6f7fa;
  color: #00A0C1;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}

.icon-svg {
  width: 20px;
  height: 20px;
  transition: filter 0.15s ease;
}

.icon-svg-fa {
  font-size: 15px;
  color: inherit;
  transition: color 0.15s ease;
}

.btn-svg-icon:hover:not(:disabled) {
  background-color: #00A0C1;
  color: #fff;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 160, 193, 0.25);
}

.btn-svg-icon:active:not(:disabled) {
  transform: translateY(0);
}

.btn-svg-icon:hover:not(:disabled) .icon-svg {
  filter: brightness(0) invert(1);
}

.btn-svg-icon:hover:not(:disabled) .icon-svg-fa {
  color: #fff !important;
}

.btn-svg-icon--delete {
  background-color: #fef2f2;
  color: #ef4444;
}

.btn-svg-icon--delete:hover:not(:disabled) {
  background-color: #ef4444;
  color: #fff;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
}

.btn-svg-icon:disabled {
  cursor: not-allowed;
  opacity: 0.4;
  transform: none !important;
  box-shadow: none !important;
}

/* ── Modern Modal & Active Job Hierarchy Styles ── */
.aj-modal-header-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background-color: #e6f7fa;
  color: #008298;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
}

.aj-modal-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.aj-header-meta {
  font-size: 0.82rem;
  color: #64748b;
}

.aj-header-sep {
  color: #cbd5e1;
}

/* ── Soft Pill Badges (Light Pastel Standard matching Leads & Contracts V2) ── */
.aj-badge-status {
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 0.78rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
  letter-spacing: 0.2px;
  line-height: 1.35;
  border: 1px solid transparent;
  transition: all 0.15s ease-in-out;
}
.aj-badge-status--completed,
.aj-badge-status--active {
  background: #ecfdf5 !important;
  color: #047857 !important;
  border: 1px solid #a7f3d0 !important;
}
.aj-badge-status--inprogress,
.aj-badge-status--in_progress {
  background: #ecfeff !important;
  color: #008298 !important;
  border: 1px solid #cffafe !important;
}
.aj-badge-status--pending,
.aj-badge-status--onhold,
.aj-badge-status--on_hold {
  background: #fffbeb !important;
  color: #b45309 !important;
  border: 1px solid #fde68a !important;
}
.aj-badge-status--cancelled,
.aj-badge-status--inactive {
  background: #fff1f2 !important;
  color: #be123c !important;
  border: 1px solid #fecdd3 !important;
}
.aj-badge-status--notconfigured,
.aj-badge-status--not_configured {
  background: #f8fafc !important;
  color: #64748b !important;
  border: 1px solid #e2e8f0 !important;
}

.aj-badge-health {
  padding: 4px 12px;
  border-radius: 50px;
  font-size: 0.78rem;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  white-space: nowrap;
  letter-spacing: 0.2px;
  line-height: 1.35;
  border: 1px solid transparent;
}
.aj-badge-health--ontrack,
.aj-badge-health--on_track {
  background: #ecfdf5 !important;
  color: #047857 !important;
  border: 1px solid #a7f3d0 !important;
}
.aj-badge-health--behindplan,
.aj-badge-health--behind_plan {
  background: #fffbeb !important;
  color: #b45309 !important;
  border: 1px solid #fde68a !important;
}
.aj-badge-health--overdue,
.aj-badge-health--atrisk,
.aj-badge-health--at_risk {
  background: #fff1f2 !important;
  color: #be123c !important;
  border: 1px solid #fecdd3 !important;
}

/* Meta Metric Cards */
.aj-card-meta {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
  height: 100%;
}

.aj-card-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
}

.aj-card-val {
  font-size: 0.92rem;
  font-weight: 700;
  color: #0f172a;
}

.aj-code-tag {
  font-family: monospace;
  font-size: 0.74rem;
  background: #f1f5f9;
  color: #475569;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.aj-trigger-badge {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 6px;
}
.aj-trigger-badge--lead { background: #e0f2fe; color: #0284c7; }
.aj-trigger-badge--contract { background: #ecfdf5; color: #059669; }
.aj-trigger-badge--manual { background: #f1f5f9; color: #475569; }

.aj-progress-text {
  font-size: 0.85rem;
  font-weight: 700;
  color: #008298;
}

.aj-progress-track {
  height: 6px;
  background-color: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  margin-top: 6px;
  margin-bottom: 4px;
}

.aj-progress-bar {
  height: 100%;
  background-color: #00A0C1;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.aj-progress-sub {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

/* Operational Tasks Section */
.aj-section-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #e6f7fa;
  color: #008298;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
}

.aj-section-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
}

.aj-count-pill {
  font-size: 0.75rem;
  font-weight: 600;
  background: #f1f5f9;
  color: #475569;
  padding: 3px 8px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

/* Task Row Cards */
.aj-task-row {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 16px 18px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  transition: all 0.2s ease;
}

.aj-task-row:hover {
  border-color: #cbd5e1;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.04);
}

.aj-step-circle {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: #f1f5f9;
  color: #0f172a;
  font-weight: 700;
  font-size: 0.82rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 1px solid #e2e8f0;
}

.aj-task-heading {
  font-size: 0.92rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 3px 0;
}

.aj-task-subdesc {
  font-size: 0.82rem;
  color: #64748b;
  margin: 0 0 8px 0;
  line-height: 1.4;
}

.aj-tags-cluster {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.aj-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 6px;
}
.aj-tag--neutral { background: #f8fafc; color: #475569; border: 1px solid #e2e8f0; }
.aj-tag--role { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; font-weight: 600; }
.aj-tag--gate { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; font-weight: 600; }
.aj-tag--integration { background: #f0fdfa; color: #0f766e; border: 1px solid #99f6e4; font-weight: 600; }

.aj-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}
.aj-status-pill--completed { background: #dcfce7; color: #15803d; border: 1px solid #bbf7d0; }
.aj-status-pill--inprogress { background: #e0f2fe; color: #0284c7; border: 1px solid #bae6fd; }
.aj-status-pill--planned { background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; }
.aj-status-pill--initiated { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }
.aj-status-pill--sm { padding: 2px 8px; font-size: 0.72rem; }

/* Subtasks Branch */
.aj-subtasks-branch {
  margin-left: 20px;
  margin-top: 12px;
  padding-left: 16px;
  border-left: 2px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.aj-subtask-pill {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.aj-substep-badge {
  font-size: 0.72rem;
  font-weight: 700;
  color: #008298;
  background: #e6f7fa;
  padding: 2px 6px;
  border-radius: 4px;
}

.aj-subtask-title {
  font-size: 0.84rem;
  font-weight: 600;
  color: #1e293b;
}

.aj-subtask-role {
  font-size: 0.75rem;
  color: #64748b;
}

/* Audit Activity Logs */
.aj-audit-list {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: #ffffff;
}

.aj-audit-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f5f9;
  font-size: 0.84rem;
}

.aj-audit-item:last-child {
  border-bottom: none;
}

.aj-actor-chip {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 600;
}
.aj-actor-chip--user { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
.aj-actor-chip--system { background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; }

.aj-audit-action {
  color: #0f172a;
}

.aj-audit-date {
  font-size: 0.78rem;
  color: #64748b;
}

/* ── Empty State ── */
.pp-empty-row {
  text-align: center;
  padding: 48px 16px !important;
}

.pp-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
}

.pp-empty-state i {
  font-size: 2rem;
  color: #cbd5e1;
}

.pp-empty-state span {
  font-size: 0.9rem;
}

/* ── Standard Spark Filters & Pagination ── */
.filterTitle {
  font-size: 0.85rem;
  font-weight: 500;
  color: #495057;
  white-space: nowrap;
}

.filterOptions {
  height: 38px;
  font-size: 13.5px;
  font-weight: 500;
  color: #334155;
  cursor: pointer;
  background-color: #f8fafc;
}

.bordered-select {
  border-radius: 8px !important;
  border: 1.5px solid #dee2e6 !important;
  padding: 6px 28px 6px 10px;
}

.bordered-select:focus {
  border-color: #00A0C1 !important;
  box-shadow: 0 0 0 3px rgba(0, 160, 193, 0.12) !important;
}

.pagination {
  display: inline-flex;
  align-items: center;
  border: 1.5px solid #dee2e6;
  border-radius: 8px;
  background-color: #ffffff;
  overflow: hidden;
  height: 38px;
  margin-bottom: 0;
}

.pagination-label {
  font-size: 0.85rem;
  color: #64748b;
  font-weight: 500;
  padding: 0 10px;
  white-space: nowrap;
}

.pagination-label strong {
  color: #1e293b;
}

.pagination-arrow {
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: #00A0C1;
  font-size: 0.85rem;
  padding: 0 12px;
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;
}

.pagination-arrow:hover:not(:disabled) {
  background-color: #f1f5f9;
}

.pagination-arrow:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}

.pp-page-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #008298;
  font-size: 0.85rem;
  padding: 6px 12px;
  transition: background-color 0.15s;
}

.pp-page-btn:hover:not(:disabled) {
  background: #e6f7fa;
}

.pp-page-btn:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}

.pp-page-label {
  font-size: 0.82rem;
  color: #64748b;
  font-weight: 500;
  padding: 0 8px;
  white-space: nowrap;
}

/* ── Modal Standard ── */
.pp-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  backdrop-filter: blur(4px);
  z-index: 1050;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px;
  overflow-y: auto;
}

.pp-modal {
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 600px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.18);
  margin: auto;
  overflow: hidden;
  animation: pp-modal-enter 0.2s ease-out;
}

.pp-modal--wide {
  max-width: 1120px;
}

.pp-modal--sm {
  max-width: 480px;
}

@keyframes pp-modal-enter {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.pp-modal__header {
  padding: 16px 20px;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pp-modal__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #00363f;
}

.pp-modal__close {
  background: none;
  border: none;
  font-size: 1.4rem;
  color: #94a3b8;
  cursor: pointer;
  line-height: 1;
}

.pp-modal__close:hover {
  color: #0f172a;
}

.pp-modal__divider {
  height: 1px;
  background: #e2e8f0;
}

.pp-modal__body {
  padding: 20px;
}

.pp-modal__actions {
  padding: 14px 20px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.pp-modal-fade-enter-active,
.pp-modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.pp-modal-fade-enter-from,
.pp-modal-fade-leave-to {
  opacity: 0;
}

/* ── Responsive ── */
@media (max-width: 992px) {
  .pp-summary-row {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .pp-summary-row {
    grid-template-columns: 1fr;
  }
  .pp-card__toolbar {
    flex-direction: column;
    align-items: stretch;
  }
  .pp-search-wrap {
    min-width: unset;
  }
  .pp-toolbar-right {
    justify-content: space-between;
  }
}
</style>
